import { toast } from "react-toastify";

import apiClient from "../../apis/api-client";
import { isPlace, Place, set } from "../../domain/proc-management";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import {
  isValidationError,
  reportProcManagementRelatedError,
} from "../../domain/proc-management/util/error";

export type ContextProps = {
  place: Place | Omit<Place, "id">;
  fetchPlaceById: (placeId: string) => Promise<void>;
  onSave: () => Promise<void>;
  onChange: (path: (string | number)[], value: any) => void;
  isUpdating: boolean;
};

export class ProcManagementPlaceUpsertService<
  P extends Record<string, any>,
> extends BaseApplicationService<P & ContextProps> {
  constructor(rest: P & Partial<ContextProps>) {
    super();
    this.state = {
      place: {
        name: "",
        organizationId: "",
      },
      fetchPlaceById: this.fetchPlaceById.bind(this),
      onSave: this.handleSave.bind(this),
      onChange: this.handleChange.bind(this),
      isUpdating: false,
      ...rest,
    };
  }

  async fetchPlaceById(placeId: string) {
    try {
      const { data } = await apiClient.procManagement.findPlaceById(placeId);
      this.setState((prev) => {
        return {
          ...prev,
          place: data,
        };
      });
    } catch (error) {
      reportProcManagementRelatedError(error);
      const message = error.response?.data?.msg ?? error.message;
      toast.error(
        `場所データの取得に失敗しました。電波状況のよい場所でリロードしてください。 placeId: ${placeId}, message: ${message}`,
      );
    }
  }

  async handleSave() {
    const { place } = this.state;
    if (!place) {
      return;
    }
    this.setState((prev) => {
      return { ...prev, isUpdating: true };
    });

    let newPlace: Place;
    const operation = isPlace(place) ? "更新" : "作成";
    try {
      if (isPlace(place)) {
        newPlace = (await apiClient.procManagement.updatePlace(place)).data;
      } else {
        newPlace = (await apiClient.procManagement.createPlace(place)).data;
      }
    } catch (error) {
      if (isValidationError(error)) {
        toast.error(
          `場所データの${operation}に失敗しました。必要な入力が完了しているか、今一度ご確認ください。`,
        );
      } else {
        reportProcManagementRelatedError(error);
        const message = error.response?.data?.msg ?? error.message;
        toast.error(`場所データの${operation}に失敗しました: ${message}`);
      }
      this.setState((prev) => {
        return {
          ...prev,
          isUpdating: false,
        };
      });
      return;
    }

    this.setState((prev) => {
      return {
        ...prev,
        place: newPlace,
        isUpdating: false,
      };
    });
    toast.success(`場所データを${operation}しました。`);
  }

  handleChange(path: (string | number)[], value: any) {
    const { place } = this.state;
    if (place) {
      set(place, path, value === "" ? null : value);
      this._formatAddress(place);
      this.setState((prev) => {
        return { ...prev, place: { ...place } };
      });
    }
  }

  private _formatAddress(place: ContextProps["place"]) {
    const { address } = place;
    if (!address) {
      return;
    }
    if (
      Object.values(address).every(
        (v) => v === "" || v === null || typeof v === "undefined",
      )
    ) {
      set(place, ["address"], null);
    }
  }
}
